import { UM_PER_MM } from "./custom-imposition.coordinates";

export const OUTPUT_NAME_MARGIN_X = "margininx";
export const OUTPUT_NAME_MARGIN_Y = "margininy";
export const OUTPUT_NAME_BLEED_X = "bleedinx";
export const OUTPUT_NAME_BLEED_Y = "bleediny";

export interface AutomaticMarginOutputs {
  marginXUm: number | null;
  marginYUm: number | null;
  bleedXUm: number | null;
  bleedYUm: number | null;
}

export function normalizeOutputName(name?: string | null): string {
  return (name || "").replace(/\s/g, "").toLowerCase();
}

export function findNamedOutput<T extends { name?: string; orginalName?: string }>(
  outputs: T[] | undefined,
  names: string[],
): T | undefined {
  const wanted = names.map(normalizeOutputName);
  return outputs?.find((output) =>
    wanted.includes(normalizeOutputName(output.orginalName || output.name)),
  );
}

export function readOutputDisplayToUm(
  output: { values?: any; value?: any; Value?: any } | undefined,
  imperial = false,
): number | null {
  if (!output) {
    return null;
  }
  const raw = Array.isArray(output.values)
    ? output.values[0]
    : output.values ?? output.value ?? output.Value;
  const display = Number(raw);
  if (!Number.isFinite(display) || display < 0) {
    return null;
  }
  return imperial ? Math.round(display * 10000) : Math.round(display * UM_PER_MM);
}

export function readAutomaticMarginsUm(
  outputs?: Array<{
    name?: string;
    orginalName?: string;
    values?: any;
    value?: any;
    Value?: any;
  }>,
  imperial = false,
): AutomaticMarginOutputs {
  return {
    marginXUm: readOutputDisplayToUm(
      findNamedOutput(outputs, [OUTPUT_NAME_MARGIN_X, "margininwidth"]),
      imperial,
    ),
    marginYUm: readOutputDisplayToUm(
      findNamedOutput(outputs, [OUTPUT_NAME_MARGIN_Y, "margininlength"]),
      imperial,
    ),
    bleedXUm: readOutputDisplayToUm(findNamedOutput(outputs, [OUTPUT_NAME_BLEED_X]), imperial),
    bleedYUm: readOutputDisplayToUm(findNamedOutput(outputs, [OUTPUT_NAME_BLEED_Y]), imperial),
  };
}

/**
 * GetImposition raise: unless the value came from a rule,
 * marginXOrginal = max(workflow.MarginX, bleedX). Same for Y.
 */
export function raiseGetImpositionMarginUm(marginUm: number, bleedUm = 0): number {
  return Math.max(Math.max(0, marginUm), Math.max(0, bleedUm));
}

/**
 * GetPrintPositions: if !withDieKissCut && !marginXByRule,
 * marginX_Copy = marginX + bleedX. Placement and CalculateProductsPerRow
 * then apply that value on EACH side (usable = sheet - 2 * marginCopy).
 */
export function resolveGetPrintPositionsInsetUm(
  getImpositionMarginUm: number,
  bleedUm = 0,
  options?: { byRule?: boolean; dieKissCut?: boolean },
): number {
  if (options?.byRule || options?.dieKissCut) {
    return Math.max(0, getImpositionMarginUm);
  }
  return Math.max(0, getImpositionMarginUm) + Math.max(0, bleedUm);
}

/** Full Automatic placement inset after GetImposition raise + GetPrintPositions copy. */
export function resolveAutomaticPlacementInsetUm(marginUm: number, bleedUm = 0): number {
  return resolveGetPrintPositionsInsetUm(raiseGetImpositionMarginUm(marginUm, bleedUm), bleedUm);
}

/** CustomImpositionValidator inset: custom.MarginX/Y only. Bleed is a separate expansion check. */
export function resolveCustomValidatorInsetUm(marginUm: number): number {
  return Math.max(0, marginUm);
}

export function firstDefinedUm(...values: Array<number | null | undefined>): number {
  for (const value of values) {
    if (value != null && Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
}
